/**
 * Итоги прогона в разрезе элементов текста.
 *
 * Режимы, которые печатают строку из нескольких слов, считают результат по
 * словам: одна ошибка в середине строки относится к тому слову, где она
 * сделана, а не ко всей строке.
 *
 * Пакет не знает, что стоит за элементом: сопоставление элемента учебной
 * единице — дело продуктовой функции.
 */

import type { KeystrokeEvent, TypingError } from '@type2x/shared-contracts';

import { median } from './metrics.js';

/** Итог прогона по одному элементу текста. */
export interface SegmentOutcome {
  /** Порядковый номер элемента в тексте, с нуля. */
  readonly index: number;
  readonly segment: string;
  readonly attempts: number;
  readonly errors: number;
  readonly corrections: number;
  readonly medianLatencyMs: number;
}

/**
 * Сопоставляет нажатия элементам текста.
 *
 * Элементы разделены одним пробелом, поэтому позиция в тексте однозначно
 * определяет, к какому элементу относится нажатие. Сам разделитель ничьим
 * элементом не считается: пробел — не учебная единица.
 *
 * @param segments элементы текста в порядке набора;
 * @param events нажатия прогона;
 * @param errors ошибки прогона.
 */
export function collectSegmentOutcomes(
  segments: readonly string[],
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
): SegmentOutcome[] {
  const errorPositions = new Set(errors.map((error) => error.position));
  const outcomes: SegmentOutcome[] = [];

  let position = 0;
  for (const [index, segment] of segments.entries()) {
    const length = [...segment].length;
    const latencies: number[] = [];
    let segmentErrors = 0;
    let corrections = 0;

    for (let offset = 0; offset < length; offset += 1) {
      if (errorPositions.has(position + offset)) segmentErrors += 1;

      const event = events[position + offset];
      if (event === undefined) continue;
      if (event.interKeyIntervalMs > 0) latencies.push(event.interKeyIntervalMs);
      if (event.corrected) corrections += 1;
    }

    outcomes.push({
      index,
      segment,
      attempts: length,
      errors: segmentErrors,
      corrections,
      medianLatencyMs: median(latencies),
    });

    // Пробел между элементами занимает одну позицию и в счёт не идёт.
    position += length + 1;
  }

  return outcomes;
}
